import {
  type AccountContact,
  type AccountData,
  type AccountStatusId,
  type DispositionId,
  type SmallAddress,
  type StatusHistoryItem,
} from '../model'
import { isEmpty, isNotEmpty } from './isEmpty'
import { sortByStatusChangedDate } from './accountStatusHistory'

export function buildLine2(address: Partial<SmallAddress>): string {
  const line2Parts = [address.locality, address.countrySubd, address.postal1].filter(isNotEmpty)
  return line2Parts.join(' ').trim()
}

export function buildLocationOneLine(location?: Partial<SmallAddress>): string {
  if (isEmpty(location)) return ''
  const { line1 = '', unitNbr = '', locality = '', countrySubd = '', postal1 = '' } = location
  const items = [line1.trim(), unitNbr.trim(), locality.trim(), countrySubd.trim(), postal1.trim()].filter(isNotEmpty)
  if (isEmpty(items)) return ''
  return items.join(', ')
}

export const getMostRecentAccountActivityId = (account: AccountData): DispositionId | AccountStatusId | undefined => {
  const mostRecentContact = getMostRecentAccountContact(account)
  const mostRecentStatus = getMostRecentAccountStatus(account)

  if (!mostRecentContact && !mostRecentStatus) return
  if (!mostRecentContact) return mostRecentStatus?.statusId
  if (!mostRecentStatus) return mostRecentContact.dispositionId
  if (mostRecentContact.timestamp >= mostRecentStatus.statusChangedDate) return mostRecentContact.dispositionId
  return mostRecentStatus.statusId
}

export const getMostRecentAccountContact = (account: AccountData): AccountContact | undefined => {
  return getMostRecentContact(account.contacts ?? [])
}

export const getMostRecentAccountStatus = (account: AccountData): StatusHistoryItem | undefined => {
  return getMostRecentStatusItem(account.statusHistory)
}

export function getMostRecentStatusItem(statusHistory: StatusHistoryItem[]): StatusHistoryItem | undefined {
  const latestItem = statusHistory.sort(sortByStatusChangedDate).at(-1)
  if (isEmpty(latestItem)) return
  return latestItem
}

export function getMostRecentContact<T extends Pick<AccountContact, 'timestamp'>>(contacts: T[]): T | undefined {
  if (isEmpty(contacts)) return undefined
  return contacts.sort((a, b) => a.timestamp - b.timestamp).at(-1)
}
